import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from 'aws-amplify/api';
import Layout from '../mui-components/Layout';
import { Link, Box, Stack, Button, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { addIds } from '../utils/helpers';
import { GridToolbar } from '@mui/x-data-grid';
import { NoRowsGrid, NoResultsGrid } from '../mui-components/NoRowsGrid';
import { pageTitles } from '../data/pageTitles';
import Loading from '../mui-components/Loading';
import { useEffect, useState } from 'react';
import { useGlobalContext } from '../context/GlobalStore';
import moment from 'moment';

import AlertModal from '../mui-components/AlertModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import apiClient from '../utils/apiClient';

export default function Devices({ getDevices }) {
  const { state, setDevices } = useGlobalContext();
  document.title = pageTitles.devices;
  const models = [...new Set(state.devices.map((item) => item.device_model))];
  const billingStatus = [
    ...new Set(state.devices.map((item) => item.billing_status)),
  ];
  const deviceTypes = [
    ...new Set(state.devices.map((item) => item.device_type)),
  ];

  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allowDeactivate, setAllowDeactivate] = useState(false);
  const [deactivateMessage, setDeactivateMessage] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filterModel, setFilterModel] = useState({ items: [] });

  const columns = [
    { field: 'device_id', headerName: 'Device Id', width: 120 },
    {
      field: 'device_imei',
      headerName: 'Imei',
      width: 140,
      renderCell: (param) => (
        <Link
          onClick={() =>
            navigate('/devices/'.concat(param.value), {
              state: {
                account_id:
                  state.account?.pylo_accounts?.find(
                    (a) => a.api_key === state.apiKey.key
                  )['id'] || '',
              },
            })
          }
        >
          {param.value}
        </Link>
      ),
    },
    {
      field: 'device_type',
      headerName: 'Device Type',
      width: 120,
      type: 'singleSelect',
      valueOptions: deviceTypes,
    },
    {
      field: 'device_model',
      headerName: 'Device Model',
      width: 140,
      type: 'singleSelect',
      valueOptions: models,
    },
    {
      field: 'first_measurement_timestamp',
      headerName: 'First Reading',
      type: 'dateTime',
      width: 170,
      valueGetter: (value) => {
        if (!value) {
          return value;
        }
        return new Date(value);
      },
      valueFormatter: (value) => {
        return value ? moment(value).format('L, LT') : value;
      },
    },
    {
      field: 'last_measurement_timestamp',
      headerName: 'Last Reading',
      type: 'dateTime',
      width: 170,
      valueGetter: (value) => {
        if (!value) {
          return value;
        }
        return new Date(value);
      },
      valueFormatter: (value) => {
        return value ? moment(value).format('L, LT') : value;
      },
    },
    {
      field: 'billing_status',
      headerName: 'Billing Status',
      width: 100,
      type: 'singleSelect',
      valueOptions: billingStatus,
    },
    {
      field: 'order_number',
      headerName: 'Order Number',
      width: 120,
      renderCell: (param) => (
        <Link onClick={() => navigate('/orders/'.concat(param.value))}>
          {param.value}
        </Link>
      ),
    },
    {
      field: 'renewal_date',
      headerName: 'Renewal Date',
      type: 'dateTime',
      width: 170,
      valueGetter: (value) => {
        if (!value) {
          return value
        }
        return new Date(value)
      },
      valueFormatter: (value) => {
        return value ? moment(value).format('L, LT') : value
      }
    },
    {
      field: 'reminders_active',
      headerName: 'Reminders Active',
      width: 100,
      type: 'singleSelect',
    }
  ];
  useEffect(() => {
    // Always set the full list of devices to the grid.
    if (state.devices) {
      const devicesWithIds = addIds(state.devices);
      setRows(devicesWithIds);
    }

    // Build the filter model based on URL search parameters.
    const filterItems = [];
    const renewalStatus = searchParams.get('renewal_status');
    const billingStatus = searchParams.get('billing_status');
    const deviceModel = searchParams.get('device_model');

    if (renewalStatus === 'pending') {
      const now = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);
      // MUI X grid filter for date range
      filterItems.push({ field: 'renewal_date', operator: 'onOrAfter', value: now.toISOString().split('T')[0] });
      filterItems.push({ field: 'renewal_date', operator: 'onOrBefore', value: thirtyDaysFromNow.toISOString().split('T')[0] });
    }

    if (billingStatus) {
      filterItems.push({ field: 'billing_status', operator: 'is', value: billingStatus });
    }
    if (deviceModel) {
      filterItems.push({ field: 'device_model', operator: 'is', value: deviceModel });
    }

    setFilterModel({ items: filterItems });
  }, [state.devices, searchParams]);

  useEffect(() => {
    if (!state?.policies?.hasOwnProperty("Admin")) {
      setAllowDeactivate(false);
      setDeactivateMessage("Only administrators can deactivate devices.")
    } else {
      const policies = Object.values(state.policies).reduce((result, inner) => {
        const apiKeyObj = Object.entries(inner).find(([key]) => key === state.apiKey.key);
        return apiKeyObj ? apiKeyObj[1] : result;
      }, [])

      if (!policies.includes("pylo:DeactivateDevices")) {
        setAllowDeactivate(false);
        setDeactivateMessage("Only administrators can deactivate devices.")
      } else {
        setAllowDeactivate(true);
        setDeactivateMessage("") 
      }
    }
  }, [state.account])

  async function sendBulkDeactivate() {
    setIsLoading(true)
    if (selectedRows.length === 0) {
      setAlertMessage("Please select at least one device to deactivate.");
      setAlertTitle("Bulk Deactivate");
      setOpenAlert(true);
      setIsLoading(false);
      return;
    }
    const rowDetails = rows.filter((r) => selectedRows.includes(r.id));
    const imeis = rowDetails.map((r) => r.device_imei);
    const device_customer_id = state?.account?.pylo_accounts?.find(
      (a) => a.api_key === state.apiKey.key
    )['id'];

    try {
      const response = await apiClient.post(
        '/devices',
        {
          action: 'deactivate_devices',
          account_id: device_customer_id,
          device_imeis: imeis
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        // update account data with new device information
        const successes = Object.keys(response["successes"]);
        const failures = Object.keys(response["failures"]);
        let message = "";
        if (successes.length > 0) {
          message += `Devices deactivated successfully: \n${successes.join(', ')}\n\n`
        }
        if (failures.length > 0) {
          message += `The following devices could not be deactivated: \n`
          for (let failure of failures) {
            message += `Device ${failure}: ${response["failures"][failure]}\n`
          }
        }
        setAlertMessage(message);
        setAlertTitle("Bulk Deactivate");
        
        let devicesCopy = state.devices;
        for (let device of devicesCopy) {
          if (device.device_imei in response["successes"]) {
            device.billing_status = "pending_deactivation";
          }
        }
        setDevices(devicesCopy);
        setOpenAlert(true);
        setIsLoading(false)
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body);
        }
      } else {
        // standard error
        errorMessage = error.message;
      }
      setAlertMessage(`Error deactivating devices: ${errorMessage}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false)
    }
  };

  return (
    <Layout title="Devices">
      <AlertModal
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <ErrorAlertModal
        open={openErrorAlert}
        handleClose={() => setOpenErrorAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <Box sx={{ height: '100%' }}>
        {state.isDeviceLoading ? (
          <Loading />
        ) : (
          <Stack gap={2} >
            <Stack direction="row" gap={2} justifyContent="space-between" display="flex" alignItems="center">
              {allowDeactivate && (
                <Button 
                variant="contained"
                onClick={sendBulkDeactivate}
                > 
                  Bulk Deactivate
                </Button>
              )}
            </Stack>

            <DataGridPro
              rows={rows}
              columns={columns}
              filterModel={filterModel}
              onFilterModelChange={(newModel) => setFilterModel(newModel)}
              slots={{
                toolbar: GridToolbar,
                noRowsOverlay: NoRowsGrid,
                noResultsOverlay: NoResultsGrid,
              }}
              pagination={true}
              checkboxSelection
              checkboxSelectionVisibleOnly
              onRowSelectionModelChange={(rowSelectionModel) => {
                setSelectedRows(rowSelectionModel);
              }}
              sx={{
                '& .MuiDataGridPro-overlay': { minHeight: 300 },
                '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
              }}
            />
          </Stack>
        )}
      </Box>
    </Layout>
  );
}
